import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { AppLayout } from './layout/AppLayout';
import { AccountsPage } from './pages/AccountsPage';
import { AchievementsPage } from './pages/AchievementsPage';
import { AuditPage } from './pages/AuditPage';
import { BackupsPage } from './pages/BackupsPage';
import { OauthLandingPage } from './pages/OauthLandingPage';
import { ContentHealthPage } from './pages/ContentHealthPage';
import { ChallengesPage } from './pages/ChallengesPage';
import { ChildrenPage } from './pages/ChildrenPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { PlansPage } from './pages/PlansPage';
import { ReadingStoriesPage } from './pages/ReadingStoriesPage';
import { ThemeModeProvider } from './theme/ThemeModeProvider';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

export const App = () => (
  <ThemeModeProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Reachable signed out: the redirect arrives before there is a session. */}
            <Route path="/auth/callback" element={<OauthLandingPage />} />

            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route
                path="/accounts"
                element={
                  <ProtectedRoute allow={['SUPPORT', 'SUPERUSER']}>
                    <AccountsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/children"
                element={
                  <ProtectedRoute allow={['SUPPORT', 'SUPERUSER']}>
                    <ChildrenPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/content-health" element={<ContentHealthPage />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              <Route path="/challenges" element={<ChallengesPage />} />
              <Route path="/reading" element={<ReadingStoriesPage />} />
              <Route
                path="/plans"
                element={
                  <ProtectedRoute allow={['SUPPORT', 'SUPERUSER']}>
                    <PlansPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/backups"
                element={
                  <ProtectedRoute allow={['SUPERUSER']}>
                    <BackupsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <ProtectedRoute allow={['SUPERUSER']}>
                    <AuditPage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ThemeModeProvider>
);
